import { alto, black, mineShaft, white } from '@parchment/atoms';
import FocusTrap from 'focus-trap-react';
import React, { useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import styled, { createGlobalStyle } from 'styled-components';
import ModalBody from './components/ModalBody';
import ModalFooter from './components/ModalFooter';
import ModalTitle from './components/ModalTitle';
export let MODAL_STATE = /*#__PURE__*/function (MODAL_STATE) {
  MODAL_STATE["DEFAULT"] = "default";
  MODAL_STATE["PROCESSING_WITH_SPINNER"] = "processingWithSpinner";
  MODAL_STATE["PROCESSING_WITH_PROGRESS"] = "processingWithProgress";
  MODAL_STATE["PROCESSING_WITH_LEGACY_SPINNER"] = "processingWithLegacySpinner";
  return MODAL_STATE;
}({});
// These are to support the modal's accessibility.
const generateUniqueId = () => Math.random().toString(36).substring(2, 9);
const getHeaderId = () => `modal-header-${generateUniqueId()}`;
const getBodyId = () => `modal-body-${generateUniqueId()}`;
const resetBodyOverflow = overflow => {
  document.body.style.overflow = overflow;
};
const Modal = _ref => {
  let {
    open = true,
    className,
    closeable = false,
    closeOnClickOverlay = true,
    defaultIcon,
    headerIcon,
    bodyIcon,
    title,
    banner,
    ariaLabel = '',
    footer,
    onClose = () => resetBodyOverflow('auto'),
    initialFocus,
    children,
    modalState = MODAL_STATE.DEFAULT,
    loadingContent,
    loadingMessage,
    loadingProgressValue,
    loadingProgressMax
  } = _ref;
  const modalRef = useRef(null);
  const modalBodyRef = useRef(null);
  const {
    current: headerId
  } = useRef(getHeaderId());
  const {
    current: bodyId
  } = useRef(getBodyId());
  const [appElement, setAppElement] = useState(null);
  const [isOpen, setIsOpen] = useState(open);
  const [isInteractiveModal, setIsInteractiveModal] = useState(modalState === MODAL_STATE.DEFAULT);
  const handleClose = () => {
    setIsOpen(false);
    onClose();
    // reset the modalState
    setIsInteractiveModal(true);
  };
  useEffect(() => {
    setIsOpen(open);
  }, [open]);
  useEffect(() => {
    resetBodyOverflow(isOpen ? 'hidden' : 'auto');
  }, [isOpen]);
  useEffect(() => {
    const isInteractiveModalState = modalState === MODAL_STATE.DEFAULT;
    setIsInteractiveModal(isInteractiveModalState);
    if (!isInteractiveModalState) {
      var _modalBodyRef$current;
      // To make this accessible especially for screen read users
      // let's focus on the body since we are changing the
      // content to let them know something has updated
      modalBodyRef === null || modalBodyRef === void 0 ? void 0 : (_modalBodyRef$current = modalBodyRef.current) === null || _modalBodyRef$current === void 0 ? void 0 : _modalBodyRef$current.focus();
    }
  }, [modalState]);
  useEffect(() => {
    let appElement = modalRef === null || modalRef === void 0 ? void 0 : modalRef.current;
    while (!!appElement && appElement.parentElement !== document.body) {
      appElement = appElement.parentElement;
    }
    setAppElement(appElement);
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    ref: modalRef
  }, /*#__PURE__*/React.createElement(ReactModal, {
    "data-testid": "react-modal",
    isOpen: isOpen,
    shouldCloseOnOverlayClick: closeOnClickOverlay && isInteractiveModal,
    shouldCloseOnEsc: closeable && isInteractiveModal,
    aria: {
      labelledby: headerId,
      describedby: bodyId
    },
    onRequestClose: handleClose,
    ariaHideApp: !!appElement,
    appElement: appElement,
    portalClassName: "ui-modal-portal",
    className: `ui-modal${className ? ` ${className}` : ''}`,
    overlayClassName: "ui-modal-overlay"
  }, /*#__PURE__*/React.createElement(GlobalStyle, null), /*#__PURE__*/React.createElement(FocusTrap, {
    focusTrapOptions: {
      initialFocus: initialFocus
    }
  }, /*#__PURE__*/React.createElement(ModalContent, null, /*#__PURE__*/React.createElement(ModalTitle, {
    title: title,
    headerIcon: headerIcon,
    defaultIcon: defaultIcon,
    closeable: closeable,
    ariaLabel: ariaLabel,
    headerId: headerId,
    handleClose: handleClose,
    isInteractiveModal: isInteractiveModal
  }), banner || null, /*#__PURE__*/React.createElement(ModalBody, {
    bodyId: bodyId,
    modalBodyRef: modalBodyRef,
    bodyIcon: bodyIcon,
    defaultIcon: defaultIcon,
    modalState: modalState,
    loadingContent: loadingContent,
    loadingMessage: loadingMessage,
    loadingProgressValue: loadingProgressValue,
    loadingProgressMax: loadingProgressMax
  }, children), /*#__PURE__*/React.createElement(ModalFooter, {
    footer: footer,
    isInteractiveModal: isInteractiveModal
  })))));
};
const GlobalStyle = /*#__PURE__*/createGlobalStyle([".ui-modal-portal{.ui-modal{display:flex;width:760px;outline:0;border:1px solid ", ";@media (max-width:767px){height:100vh;width:100vw;}}.ui-modal-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background-color:", ";display:flex;flex-direction:column;justify-items:center;justify-content:center;align-items:center;z-index:10;}}"], _ref2 => {
  let {
    theme
  } = _ref2;
  return (theme === null || theme === void 0 ? void 0 : theme.borderColor) || alto;
}, `${black}b3`);
const ModalContent = /*#__PURE__*/styled.div.withConfig({
  displayName: "Modal__ModalContent",
  componentId: "sc-1pmdncz-0"
})(["display:flex;flex-direction:column;justify-content:stretch;align-items:stretch;background-color:", ";max-height:600px;min-height:480px;width:760px;color:", ";@media (max-width:767px){max-height:100vh;height:100vh;width:100vw;}@media (max-width:767px),(max-height:600px){height:100vh;}"], _ref3 => {
  var _theme$whiteSpace;
  let {
    theme
  } = _ref3;
  return (theme === null || theme === void 0 ? void 0 : (_theme$whiteSpace = theme.whiteSpace) === null || _theme$whiteSpace === void 0 ? void 0 : _theme$whiteSpace.base) || white;
}, _ref4 => {
  var _theme$fontColors, _theme$fontColors$bod;
  let {
    theme
  } = _ref4;
  return (theme === null || theme === void 0 ? void 0 : (_theme$fontColors = theme.fontColors) === null || _theme$fontColors === void 0 ? void 0 : (_theme$fontColors$bod = _theme$fontColors.body) === null || _theme$fontColors$bod === void 0 ? void 0 : _theme$fontColors$bod.base) || mineShaft;
});
export default Modal;